import { useMemo, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import BankAccountInformation from "./bank-account-information";
import BankingActionButtons from "./banking-action-buttons";
import NextSteps from "./next-steps";
import BankingPopupActivity from "./banking-popup-activity";
import BankingServices from "./banking-services";
import SpendTracker from "./spend-tracker";
import MoveInBonusTracker from "./move-in-bonus-tracker";
import MoveInCashUnavailableNotice from "./move-in-cash-unavailable-notice";
import ProfileBar from "../../Organisms/App Utility Bar/profilebar";
import HomeCard from "../../Organisms/Home Card";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";
import { useModalContext } from "../../Contexts/ModalContext";

import BankAccountInformationSkeleton from "../../Components/Skeleton/bank-account-information-skeleton";
import BankActionButtonsSkeleton from "../../Components/Skeleton/banking-action-buttons-skeleton";
import NextStepsSkeleton from "../../Components/Skeleton/next-steps-skeleton";
import useAstra from "../../CustomHooks/useAstra";
import usePinWheel from "../../CustomHooks/usePinWheel";

import { useActivities, useStakeDebit, useConnectedAccounts, useCreditReportDetails, useDeals } from "../../CustomHooks/Queries";

import {
  BATTLECARD_FLOW_NAV_STATE,
  getBattlecardRedirectNavState,
  getLegacyCheckRouteFromDealsResponse,
  isLegacyCheckDeliveredFromDealsResponse,
  isLegacyCheckPendingFromDealsResponse,
  isLegacyMoveInCashPlanFromDealsResponse,
  isSVDApproved,
  LocalStorage,
  shouldRedirectToBattlecardPage,
  shouldShowMoveInBonusTrackerFromDealsResponse,
  shouldShowMoveInCashUnavailableModalFromDealsResponse,
  shouldShowMoveInCashUnavailableNoticeFromDealsResponse,
  shouldShowUnclaimedPage,
  getBattlecardSpendTrackerFromDealsResponse,
  isBattlecardUserFromDealsResponse,
} from "../../Utils/helpers";
import { APIQueryKeys, appUtilityKeys } from "../../Utils/constants";
import {
  getMockStakeDebit,
  getMockPayrollAccounts,
  getMockCreditReporting,
  getMockConnectedAccounts,
  getMockSpendData,
  getMockActivities,
  getMockRentDetails,
} from "../../Utils/admin-mock-data";

import { useAdminStateSafe } from "../../Contexts/AdminStateContext";

import { CircleExclamationOutlined } from "../../Assets/Icons";
import CloseButton from "../../Components/Buttons/close-button";
import renderMoveInCashUnavailable from "../../Organisms/Modals/Move In Cash/move-in-cash-unavailable";

import "./banking.scss";
import "./banking-responsive.scss";
import "./banking-skeleton.scss";
import "../Welcome/stake-ai-chat.scss";
import { StakeCheckingProgressIndexToStepKey } from "../../Organisms/Modals/Stake Checking Status/stake-checking-onboarding-step";

const DEFAULT_SPEND_DATA = {
  pct: 0,
  spent: "$0",
  progressDesc: "$250 more to unlock",
  ctaGray: "Spend $250 this month to earn",
  ctaGreen: "$10 Cash Back on Rent",
};

function useTypewriter(text = "", speed = 9) {
  const [display, setDisplay] = useState("");
  useEffect(() => {
    setDisplay("");
    let idx = 0;
    let timer = null;
    const tick = () => {
      setDisplay(text.slice(0, idx + 1));
      idx += 1;
      if (idx < text.length) {
        timer = setTimeout(tick, speed);
      }
    };
    timer = setTimeout(tick, speed);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [text, speed]);
  return display;
}

function ChatBubble({ role = "system", children, delayed = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      if (ref.current) ref.current.classList.add("visible");
    }, delayed);
    return () => clearTimeout(t);
  }, [delayed]);
  return (
    <div ref={ref} className={`stake-ai-bubble ${role} fade-in`}>
      {role === "system" && (
        <div className="avatar">
          <img src="/favicon-32x32.png" alt="Stake" />
        </div>
      )}
      <div className="bubble-inner">{children}</div>
    </div>
  );
}

export default function Banking() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const adminState = useAdminStateSafe();
  const { setAppUtilityProps } = useAppUtilityContext();
  const modalContext = useModalContext();
  const { modalVisibility } = modalContext;
  const hasOpenedMoveInCashUnavailableModal = useRef(false);

  useEffect(() => {
    if (!LocalStorage.read(appUtilityKeys.transferToken)) return;

    let cancelled = false;

    queryClient.invalidateQueries(APIQueryKeys.unclaimedTransactions).then(() => {
      if (cancelled) return;

      const unclaimedData = queryClient.getQueryData(APIQueryKeys.unclaimedTransactions);
      const dealsData = queryClient.getQueryData(APIQueryKeys.deals);
      const stakeDebitData = queryClient.getQueryData(APIQueryKeys.stakeDebit);

      if (shouldShowUnclaimedPage(dealsData, unclaimedData)) {
        navigate("/unclaimed");
      } else if (shouldRedirectToBattlecardPage(dealsData, unclaimedData, stakeDebitData)) {
        navigate("/battlecard", {
          state: getBattlecardRedirectNavState(dealsData, unclaimedData, stakeDebitData),
        });
      } else if (isLegacyCheckPendingFromDealsResponse(dealsData)) {
        navigate(getLegacyCheckRouteFromDealsResponse(dealsData), {
          state: BATTLECARD_FLOW_NAV_STATE,
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [queryClient, navigate]);

  const urlParams = new URLSearchParams(location.search);
  const fromWelcome = urlParams.get("fromWelcome") === "true";

  const { isLoading: isLoadingActivities, data: activitiesDataLive } = useActivities();

  const { isLoading: isLoadingConnectedAccounts, data: ConnectedAccountsLive } = useConnectedAccounts();

  const { isLoading: isLoadingDeals, data: deals } = useDeals();

  const isLegacyCheckDelivered = useMemo(
    () => !isLoadingDeals && isLegacyCheckDeliveredFromDealsResponse(deals),
    [deals, isLoadingDeals]
  );

  useEffect(() => {
    if (!isLegacyCheckDelivered) return;

    setAppUtilityProps((p) => ({
      ...p,
      hideNavigationBar: false,
      lockedRoutes: [],
    }));
  }, [isLegacyCheckDelivered, setAppUtilityProps]);

  useEffect(() => {
    if (isLoadingDeals || !isLegacyCheckPendingFromDealsResponse(deals)) return;
    navigate(getLegacyCheckRouteFromDealsResponse(deals), {
      replace: true,
      state: BATTLECARD_FLOW_NAV_STATE,
    });
  }, [deals, isLoadingDeals, navigate]);

  useEffect(() => {
    if (
      isLoadingDeals ||
      modalVisibility ||
      hasOpenedMoveInCashUnavailableModal.current ||
      !shouldShowMoveInCashUnavailableModalFromDealsResponse(deals)
    ) {
      return;
    }

    hasOpenedMoveInCashUnavailableModal.current = true;
    renderMoveInCashUnavailable(modalContext);
  }, [deals, isLoadingDeals, modalContext, modalVisibility]);

  const { isLoading: isLoadingStakeDebitLive, data: stakeDebitLive } = useStakeDebit();

  const IsSVDApprovedLive = useMemo(() => isSVDApproved(stakeDebitLive?.data), [stakeDebitLive?.data]);

  const { isLoadingPayrollAccounts, payrollAccountsData: payrollAccountsDataLive, ...payrollProps } = usePinWheel(IsSVDApprovedLive);

  const { isLoading: isLoadingCreditReporting, data: creditReportingLive } = useCreditReportDetails();

  const { isInitialisingAstra, isLoadingAstraCards, astraDebitCards } = useAstra();

  const isMock = adminState?.isMockMode;
  const isWallet = isMock && adminState.selUserType === "usertype-wallet";
  const isStakeChecking = isMock && adminState.selUserType === "usertype-stake-checking";
  const effectiveChecking = (isStakeChecking || isWallet) ? "checking-done" : adminState?.selChecking;
  const stakeDebit = isMock ? getMockStakeDebit(effectiveChecking) : stakeDebitLive;
  const stakeDebitForNextSteps = isMock ? getMockStakeDebit(adminState?.selChecking) : stakeDebitLive;
  const payrollAccountsData = isMock ? getMockPayrollAccounts(adminState.selDirectDeposit) : payrollAccountsDataLive;
  const creditReporting = isMock ? getMockCreditReporting(adminState.selCreditBuilder) : creditReportingLive;
  const ConnectedAccounts = isMock ? getMockConnectedAccounts(adminState.selConnectedAccounts) : ConnectedAccountsLive;
  const activitiesData = isMock ? getMockActivities() : activitiesDataLive;
  const spendData = isMock ? getMockSpendData(adminState.selSpend) : DEFAULT_SPEND_DATA;
  const mockRentDetails = isMock ? getMockRentDetails(adminState.selCashback) : null;
  const showSpendTracker = isMock ? mockRentDetails?.data?.data?.my_return_on_rent?.is_my_return_on_rent_visible !== false : true;

  const isLoadingStakeDebit = isMock ? false : isLoadingStakeDebitLive;

  const shouldHideQuickActions = useMemo(
    () =>
      !isLoadingDeals &&
      isBattlecardUserFromDealsResponse(deals) &&
      isLegacyMoveInCashPlanFromDealsResponse(deals),
    [deals, isLoadingDeals],
  );

  const showMoveInBonusTracker = useMemo(
    () => !isLoadingDeals && shouldShowMoveInBonusTrackerFromDealsResponse(deals),
    [deals, isLoadingDeals],
  );

  const showMoveInCashUnavailableNotice = useMemo(
    () => !isLoadingDeals && shouldShowMoveInCashUnavailableNoticeFromDealsResponse(deals),
    [deals, isLoadingDeals],
  );

  const moveInCashPlan = deals?.data?.data?.currentDeal?.move_in_cash_plan;
  const battlecardSpendTracker = getBattlecardSpendTrackerFromDealsResponse(deals);

  const isLoadingBankingActionButtons = isMock
    ? false
    : isLoadingDeals ||
      isLoadingStakeDebit ||
      isLoadingConnectedAccounts ||
      isLoadingAstraCards ||
      isInitialisingAstra;

  const isLoadingNextSteps = isMock
    ? false
    : isLoadingStakeDebit ||
      isLoadingActivities ||
      isLoadingPayrollAccounts ||
      isLoadingCreditReporting ||
      isLoadingConnectedAccounts;

  const isLoadingBankingServices = isMock
    ? false
    : isLoadingStakeDebit || isLoadingDeals ||
      isLoadingConnectedAccounts ||
      isLoadingPayrollAccounts ||
      isLoadingAstraCards ||
      isInitialisingAstra;

  const availableBalance = useMemo(() => {
    return stakeDebit?.data?.data?.available ?? 0;
  }, [stakeDebit]);

  const welcomeMessage = t("banking.welcomeMessage", {
    defaultValue:
      'Welcome to your new Stake account! Now you\'ll earn Cash Back each month you pay rent using Stake. Now, Connect your paycheck by clicking "Express Paycheck" below, or Click on Add Cash to connect an external bank and fund your account now',
  });
  const typedMessage = useTypewriter(fromWelcome ? welcomeMessage : "", 9);
  const [showChat, setShowChat] = useState(true);
  const [popup, setPopup] = useState(null);

  const [checkingStep, setCheckingStep] = useState(null);

  useEffect(() => {
    setCheckingStep(() => {
      const progressState = deals?.data?.data?.currentDeal?.stake_checking_progress_state;
      return StakeCheckingProgressIndexToStepKey[progressState > 4 ? progressState + 1 : progressState] || null;
    });
  }, [deals]);
  
  useEffect(() => {
    if (isMock) setCheckingStep(adminState?.selChecking || null);
  }, [isMock, adminState?.selChecking]);

  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);
  const isMainContentLoaded =
    !isLoadingActivities &&
    !isLoadingStakeDebit &&
    (shouldHideQuickActions || !isLoadingBankingActionButtons) &&
    !isLoadingNextSteps;

  useEffect(() => {
    if (isMainContentLoaded && !hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, [isMainContentLoaded]);

  const bankingPage = (
    <div className={`banking${animReady ? " anim-ready" : ""}`}>
      {fromWelcome && showChat && (
        <div
          className="banking-welcome-chat"
          style={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            width: "360px",
            maxHeight: "500px",
            overflowY: "auto",
            zIndex: 1000,
            background: "#171b26",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          }}
        >
          <CloseButton onClick={() => setShowChat(false)} className="chat-close-btn" />
          <ChatBubble role="system" delayed={300}>
            <p className="typewriter">{typedMessage}</p>
          </ChatBubble>
        </div>
      )}
      {/* <div className="page-banner error">
        <CircleExclamationOutlined />
        <div className="message">
          Action required — To continue using your Stake Checking account, you’ll need to migrate to our new banking
          partner. <button className="action">Migrate Now →</button>
        </div>
      </div> */}
      <section>
        <div className="banking-details max-width-container">
          <ProfileBar />
          {isLoadingActivities || isLoadingStakeDebit ? null : (
            <BankAccountInformation
              currentBalance={activitiesData?.data?.data?.currentBalance ?? 0}
              lifetimeEarnings={activitiesData?.data?.data?.lifetimeEarnings ?? 0}
              availableBalance={availableBalance}
              stakeDebit={stakeDebit}
              onViewActivity={() => setPopup('activity')}
              isWallet={isWallet}
            />
          )}
          {!shouldHideQuickActions &&
            (isLoadingBankingActionButtons ? (
              <BankActionButtonsSkeleton />
            ) : (
              <BankingActionButtons
                stakeDebit={stakeDebit}
                connectedAccounts={ConnectedAccounts}
                astraDebitCards={astraDebitCards}
                fromWelcome={fromWelcome}
              />
            ))}
          {showMoveInBonusTracker && (
            <MoveInBonusTracker plan={moveInCashPlan} tracker={battlecardSpendTracker} />
          )}
          {showMoveInCashUnavailableNotice && <MoveInCashUnavailableNotice />}
          {isLoadingNextSteps ? (
            <NextStepsSkeleton />
          ) : (
            <NextSteps
              stakeDebit={stakeDebitForNextSteps}
              payrollAccountsData={payrollAccountsData}
              creditReporting={creditReporting}
              connectedAccountsData={ConnectedAccounts}
              activitiesData={activitiesData}
              checkingStep={checkingStep}
            />
          )}
          {/* Hidden for v1 — cashback spend tracker disabled for all cohorts */}
          {/* {showSpendTracker && <SpendTracker data={spendData} />} */}
          <HomeCard creditReporting={creditReporting} />
          {!shouldHideQuickActions && !isLoadingBankingServices && (
            <BankingServices stakeDebit={stakeDebit} payrollAccountsData={payrollAccountsData} />
          )}
        </div>
      </section>
      <BankingPopupActivity
        open={popup === 'activity'}
        onClose={() => setPopup(null)}
        activitiesData={activitiesData}
      />
    </div>
  );

  return bankingPage;
}
